/* ESNC motion — scroll choreography for the HLAYA page.
 *
 * The score is one line from the bottle: "Energy that moves inward." Things
 * gather as you read — the hero closes into a card, letters draw together,
 * pictures open from a window to their full frame — instead of bouncing in
 * from the edges.
 *
 * How it works: every element marked [data-m] gets a progress value written
 * as CSS custom properties, and esnc-motion.css turns those numbers into
 * transforms and clip-paths. JavaScript never touches a transform itself, so
 * the page is one cheap style write per moving element per frame.
 *
 *   --p  0 → 1 while the element travels from `data-m-from` (a fraction of
 *        the viewport height, default .96) to `data-m-to` (default .42),
 *        measured at its top edge. Enter effects.
 *   --x  0 → 1 while the element's own box scrolls out through the top of
 *        the screen ([data-m-exit]). Exit effects (the hero, the film).
 *
 *   data-m-once   reading text: once it has filled, it stays filled
 *   data-m-done   set while --p is 1, so finished text drops its transforms
 *   data-m-out    set on an exit element once its words have faded
 *
 * Every variable and attribute the engine writes starts with m- so it can
 * never meet a name the page already uses (sport-home.css registers --mx).
 *
 * Without JavaScript, or with reduced motion, nothing is ever set, and every
 * rule in the stylesheet falls back to the finished state — the page reads
 * exactly as laid out. Splitting ([data-m-split]) only wraps words in spans;
 * the text, its markup and its order stay as written, so screen readers and
 * search read the same sentence.
 *
 * Layout is read only when it changes (load, fonts, resize, a ResizeObserver
 * on the page), never inside the scroll frame, so a frame is arithmetic and
 * the few style writes whose values moved. Lenis smooths the real window
 * scroll, so window.scrollY is the truth either way.
 */
(function () {
  'use strict';

  // one engine runs the page, however many sections load this file
  if (window.__esncMotion) return;
  window.__esncMotion = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;

  var html = document.documentElement;
  html.classList.add('m-on');

  var items = [];       // { el, from, to, enter, exit, once, top, h, p, x, land }
  var raf = 0, dirty = true, vh = window.innerHeight, running = true;

  function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* --- splitting ---------------------------------------------------------
     words: each word in <span class="m-w"><span class="m-wi">word</span></span>
     with --i as its index, the spaces kept as text. lines: split on the
     author's own line breaks (the statements are set with white-space:
     pre-line), each line wrapped the same way, inline markup such as the
     statement's <em> carried into its line whole. chars: one span per
     letter, used only on the bottle name. */
  function splitWords(el) {
    var idx = 0;
    var walk = function (node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var parts = n.nodeValue.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement('span'); w.className = 'm-w'; w.style.setProperty('--i', idx++);
            var wi = document.createElement('span'); wi.className = 'm-wi'; wi.textContent = part;
            w.appendChild(wi); frag.appendChild(w);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !n.classList.contains('m-w') && n.tagName !== 'CITE') {
          walk(n);
        }
      });
    };
    walk(el);
    el.style.setProperty('--n', idx);
  }
  function splitLines(el) {
    var lines = [[]];
    Array.prototype.slice.call(el.childNodes).forEach(function (n) {
      if (n.nodeType === 3) {
        n.nodeValue.split('\n').forEach(function (part, k) {
          if (k) lines.push([]);
          if (part) lines[lines.length - 1].push(document.createTextNode(part));
        });
      } else {
        lines[lines.length - 1].push(n);
      }
    });
    lines = lines.filter(function (l) {
      return l.some(function (n) { return n.nodeType === 1 || /\S/.test(n.nodeValue); });
    });
    el.textContent = '';
    lines.forEach(function (nodes, i) {
      var l = document.createElement('span'); l.className = 'm-l'; l.style.setProperty('--i', i);
      var li = document.createElement('span'); li.className = 'm-li';
      nodes.forEach(function (n, k) {
        // trim the line's outer spaces; the text between stays as written
        if (n.nodeType === 3) {
          var v = n.nodeValue;
          if (k === 0) v = v.replace(/^\s+/, '');
          if (k === nodes.length - 1) v = v.replace(/\s+$/, '');
          n.nodeValue = v;
        }
        li.appendChild(n);
      });
      l.appendChild(li); el.appendChild(l);
      // a plain space between the line blocks keeps the sentence readable
      // as text; a newline here would add an empty line under pre-line
      if (i < lines.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.style.setProperty('--n', lines.length);
  }
  function splitChars(el) {
    var text = el.textContent.replace(/^\s+|\s+$/g, '');
    var chars = Array.from ? Array.from(text) : text.split('');
    var n = chars.length;
    el.setAttribute('aria-label', text);
    el.textContent = '';
    for (var i = 0; i < n; i++) {
      var c = document.createElement('span'); c.className = 'm-c'; c.setAttribute('aria-hidden', 'true');
      c.textContent = chars[i];
      // 0 at the first letter, 1 at the last: the letters close in on the
      // first, which stays on the page's edge
      c.style.setProperty('--d', (i / (n - 1 || 1)).toFixed(3));
      c.style.setProperty('--i', i);
      el.appendChild(c);
    }
    el.style.setProperty('--n', n);
  }

  /* --- registry ------------------------------------------------------------ */
  function register(el) {
    if (el.__m) return;
    el.__m = true;
    var split = el.getAttribute('data-m-split');
    if (split === 'words') splitWords(el);
    else if (split === 'lines') splitLines(el);
    else if (split === 'chars') splitChars(el);
    items.push({
      el: el,
      from: parseFloat(el.getAttribute('data-m-from') || '.96'),
      to: parseFloat(el.getAttribute('data-m-to') || '.42'),
      enter: el.hasAttribute('data-m'),
      exit: el.hasAttribute('data-m-exit'),
      once: el.hasAttribute('data-m-once'),
      top: 0, h: 1, p: -1, x: -1, out: false, done: false
    });
  }

  /* --- layout cache --------------------------------------------------------
     Each item's document top and height. An element's own motion (a rise,
     a drift) must not feed back into its progress, so while measuring,
     html[data-m-measure] parks those translates at rest. It is set and
     cleared inside one frame, before anything paints. */
  function measure() {
    vh = window.innerHeight;
    html.setAttribute('data-m-measure', '');
    var sy = window.scrollY;
    for (var i = 0; i < items.length; i++) {
      var r = items[i].el.getBoundingClientRect();
      items[i].top = r.top + sy;
      items[i].h = Math.max(1, r.height);
    }
    html.removeAttribute('data-m-measure');
    dirty = false;
  }
  function invalidate() { dirty = true; ask(); }

  function frame() {
    raf = 0;
    if (!running) return;
    if (dirty) measure();
    var sy = window.scrollY;
    for (var i = 0; i < items.length; i++) {
      var it = items[i], top = it.top - sy;
      if (it.enter && !(it.once && it.p === 1)) {
        var p = clamp((it.from * vh - top) / ((it.from - it.to) * vh));
        if (it.land !== undefined) p = Math.max(p, it.land);
        p = Math.round(p * 1000) / 1000;
        if (p !== it.p) {
          it.p = p; it.el.style.setProperty('--p', p);
          if ((p === 1) !== it.done) { it.done = p === 1; it.el.toggleAttribute('data-m-done', it.done); }
        }
      }
      if (it.exit) {
        var x = Math.round(clamp(-top / it.h) * 1000) / 1000;
        if (x !== it.x) {
          it.x = x; it.el.style.setProperty('--x', x);
          // past the point where its words have faded, the section says so,
          // and the stylesheet stops its controls taking focus or clicks
          if ((x > .22) !== it.out) { it.out = x > .22; it.el.toggleAttribute('data-m-out', it.out); }
        }
      }
    }
  }
  function ask() { if (!raf) raf = window.requestAnimationFrame(frame); }

  /* --- landing -------------------------------------------------------------
     Scroll drives everything, but whatever is already on screen when the
     page opens (the statement under a phone's hero, or anything after a
     reload mid-page) would otherwise sit half-revealed until the first
     scroll. Those play in once on a short timer, after the hero's own
     words, and then stay finished. */
  function land() {
    var list = items.filter(function (it) {
      if (!it.enter) return false;
      var top = it.top - window.scrollY;
      return top < vh && top + it.h > 0;
    });
    if (!list.length) return;
    list.forEach(function (it) { it.land = 0; });
    var t0 = 0;
    var tick = function (now) {
      if (!running) return;
      if (!t0) t0 = now;
      var k = clamp((now - t0 - 900) / 1300);
      list.forEach(function (it) { it.land = k; });
      frame();
      if (k < 1) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-m], [data-m-exit]'), register);
    measure();
    land();
    frame();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(invalidate);
      ro.observe(document.body);
      items.forEach(function (it) { ro.observe(it.el); });
    }
    // Lenis moves the real window scroll, so the native event is enough and
    // keeps working when Lenis is off (touch scrolling stays native)
    window.addEventListener('scroll', ask, { passive: true });
    window.addEventListener('resize', invalidate);
    window.addEventListener('load', invalidate);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(invalidate);
  }

  // someone turns reduced motion on mid-visit: drop to the finished page
  var stop = function () {
    if (!reduce.matches) return;
    running = false;
    html.classList.remove('m-on');
    items.forEach(function (it) {
      it.el.style.removeProperty('--p'); it.el.style.removeProperty('--x');
      it.el.removeAttribute('data-m-done'); it.el.removeAttribute('data-m-out');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-m-magnet]'), function (t) {
      t.style.removeProperty('--m-mx'); t.style.removeProperty('--m-my');
    });
    items = [];
  };
  if (reduce.addEventListener) reduce.addEventListener('change', stop);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* --- magnetic buttons ----------------------------------------------------
     The page's calls to action lean a few pixels toward a fine pointer, and
     settle back when it leaves. Hover-capable, fine pointers only. */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('pointermove', function (e) {
      if (!running) return;
      var t = e.target && e.target.closest ? e.target.closest('[data-m-magnet]') : null;
      if (!t) return;
      var b = t.getBoundingClientRect();
      var dx = (e.clientX - (b.left + b.width / 2)) / b.width, dy = (e.clientY - (b.top + b.height / 2)) / b.height;
      t.style.setProperty('--m-mx', (dx * 8).toFixed(2) + 'px');
      t.style.setProperty('--m-my', (dy * 6).toFixed(2) + 'px');
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-m-magnet]') : null;
      if (t && !t.contains(e.relatedTarget)) { t.style.setProperty('--m-mx', '0px'); t.style.setProperty('--m-my', '0px'); }
    }, { passive: true });
  }
}());
